/**
 * Live chat panel.
 *
 * The chat itself is the separate chat app, embedded in an iframe. This module
 * owns three things and nothing else: where the panel sits, when it may exist,
 * and the toggle in the control bar.
 *
 * WHERE IT SITS. The panel is a sibling of the player inside the stage (the
 * `.app` container), not a child of the player element. That keeps every
 * video.js internal untouched: the player is simply inset from one edge and
 * the panel occupies what it gives up. The cost is fullscreen, which video.js
 * would apply to the player element alone — player.ts therefore fullscreens
 * the STAGE, so both columns go together (see makeFullscreenUseStage).
 *
 * WHEN IT MAY EXIST. The iframe is built only from a grant. A viewer who was
 * refused never reaches this code, and the panel carries the session id so the
 * chat app can verify the join against watch-guard rather than trusting an
 * embed id that anyone can read out of the page URL.
 *
 * Offline is different from refused: the panel is torn down when the broadcast
 * is confirmed over, and comes back with it. Everything before that — booting,
 * resolving, buffering, a player that has not painted a frame yet — keeps the
 * chat, because a room that disappears while the video is still loading reads
 * as breakage.
 */

const CHAT_BASE = import.meta.env.VITE_CHAT_ENDPOINT ?? 'https://livechat.livepush.io'

/** Remembered per embed: the viewer's last open/closed choice. */
const OPEN_KEY_PREFIX = 'lp_chat_open_'

/**
 * Remembered per embed: how big the viewer dragged the panel, as a FRACTION of
 * the stage rather than a pixel count. A viewer who sizes the chat on a 1400px
 * window and comes back on an 900px one wants the same proportion, not the
 * same number of pixels, and the same store then serves both orientations.
 */
const SIZE_KEY_PREFIX = 'lp_chat_size_'

/**
 * How far the split may be dragged, as a fraction of the stage.
 *
 * The floors are not politeness. Below about a fifth the chat is a column of
 * broken words, and above two thirds the video is a postage stamp on a page
 * that exists to show video. The CSS keeps a pixel floor underneath these for
 * the case where the player itself is tiny.
 */
const SPLIT_MIN = 0.2
const SPLIT_MAX = 0.66

/** Keyboard nudge per press, as a fraction of the stage. */
const SPLIT_STEP = 0.03

const ICON_GRIP =
  '<svg viewBox="0 0 12 40" fill="currentColor" aria-hidden="true">' +
  '<rect x="4" y="8" width="4" height="24" rx="2"/></svg>'

/** Beyond this the badge stops counting and says "lots". */
const UNREAD_CAP = 99

/**
 * Messages the chat app may send us. The player cannot see inside the iframe —
 * it is cross-origin, deliberately — so anything we know about the room's
 * state can only come from the chat app volunteering it.
 *
 *   CLOSE, from the chat app:  { type: 'livepush:chat:close' }
 *     Contracted in the chat project's PLAYER_INTEGRATION.md §5. The chat has
 *     its own dismiss affordance and deliberately does not hide itself; it
 *     reports the intent and we decide what closing means. Here it means the
 *     same as our own header button, so the viewer's choice is remembered.
 *
 *   UNREAD, from the chat app:  { type: 'livepush:chat', unread: <int> }
 *   SEEN, to the chat app:      { type: 'livepush:chat', action: 'seen' }
 *     NOT in the contract. The chat app does not send unread counts today, so
 *     the badge stays dark and the seen ack lands on a listener that is not
 *     there. Both are harmless in that state and are kept wired so the badge
 *     lights up the day the chat side adds the message, rather than needing
 *     the feature rebuilt. See CHAT.md.
 *
 * Everything about an inbound message is treated as hostile until proven
 * otherwise: it must come from our own frame's window AND from the chat
 * origin, and the count is coerced to a bounded integer. A page can post to
 * any window it has a handle on, so neither check alone is enough.
 */
const CHAT_MESSAGE = 'livepush:chat'
const CHAT_CLOSE_MESSAGE = 'livepush:chat:close'

const ICON_CLOSE =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg>'

/*
  One icon, two fills. Off: the bubble is an outline with two solid lines
  inside it. On: the same silhouette goes solid and the lines become holes
  punched through it (fill-rule evenodd), so the active state reads as the
  inverse of the resting one rather than as a different icon in a different
  colour. CSS swaps which path is painted.
*/
const BUBBLE_SILHOUETTE = 'M20 2H4a2 2 0 0 0-2 2v18l4-4h14a2 2 0 0 0 2-2V4a2 2 0 0 0-2-2Z'
const BUBBLE_LINES = 'M6.5 7.5h11v1.6h-11zM6.5 11h7.5v1.6H6.5z'

const ICON_CHAT =
  '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
  '<path class="lp-chat-outline" d="' + BUBBLE_SILHOUETTE.slice(0, -1) +
  'Zm0 14H5.2L4 17.2V4h16v12Z"/>' +
  '<path class="lp-chat-lines" d="' + BUBBLE_LINES + '"/>' +
  '<path class="lp-chat-solid" fill-rule="evenodd" d="' + BUBBLE_SILHOUETTE + BUBBLE_LINES + '"/>' +
  '</svg>'

export interface ChatOptions {
  embedId: string
  sessionId: string
}

function recallOpen(embedId: string): boolean {
  try {
    return window.localStorage.getItem(OPEN_KEY_PREFIX + embedId) === '1'
  } catch {
    return false
  }
}

function rememberOpen(embedId: string, open: boolean): void {
  try {
    window.localStorage.setItem(OPEN_KEY_PREFIX + embedId, open ? '1' : '0')
  } catch {
    /* private window, blocked storage — the choice just does not persist */
  }
}

function recallSize(embedId: string): number | null {
  try {
    const raw = window.localStorage.getItem(SIZE_KEY_PREFIX + embedId)
    const n = raw === null ? NaN : Number(raw)
    return Number.isFinite(n) && n >= SPLIT_MIN && n <= SPLIT_MAX ? n : null
  } catch {
    return null
  }
}

function rememberSize(embedId: string, fraction: number): void {
  try {
    window.localStorage.setItem(SIZE_KEY_PREFIX + embedId, fraction.toFixed(4))
  } catch {
    /* private window, blocked storage — the size just does not persist */
  }
}

/**
 * The room URL.
 *
 * `room` and `session` are the only parameters the chat app reads (its §2);
 * anything else we appended would be ignored. Read-only is NOT passed: the
 * chat resolves that from its own room config, and a player-supplied flag
 * would only be a second opinion the chat has no reason to trust.
 *
 * `session` is a single-use handoff id. The chat spends it once for an access
 * token and deletes it, and an id it does not recognise degrades the viewer to
 * anonymous watching rather than erroring — so a working room is NOT evidence
 * the handoff landed.
 */
export function chatRoomUrl(opts: ChatOptions): string {
  const url = new URL(CHAT_BASE)
  url.searchParams.set('room', opts.embedId)
  if (opts.sessionId) url.searchParams.set('session', opts.sessionId)
  return url.toString()
}

export class ChatPanel {
  private readonly stage: HTMLElement
  /** Mutable: the session id can be renewed under us. */
  private opts: ChatOptions
  private panel: HTMLElement | null = null
  private button: HTMLButtonElement | null = null
  private unavailable = false
  private open = false
  private unread = 0
  private badge: HTMLElement | null = null
  private onFrameMessage: ((ev: MessageEvent) => void) | null = null
  /** The viewer's choice, kept across an offline gap so it can be restored. */
  private wanted = false
  /** The split, as a fraction of the stage. null means "whatever CSS says". */
  private size: number | null = null
  private resizer: HTMLElement | null = null
  private releaseResize: (() => void) | null = null
  private stackQuery: MediaQueryList | null = null
  private onStackChange: (() => void) | null = null

  constructor(stage: HTMLElement, opts: ChatOptions) {
    this.stage = stage
    this.opts = opts
    this.wanted = recallOpen(opts.embedId)
    this.size = recallSize(opts.embedId)

    /*
      The same fraction means a width in one orientation and a height in the
      other. Rotating a phone therefore has to move the value onto the other
      axis, or the panel keeps a width the CSS is no longer reading and loses
      the height it now needs.
    */
    try {
      this.stackQuery = window.matchMedia('(max-aspect-ratio: 1/1)')
      this.onStackChange = () => this.applySize()
      this.stackQuery.addEventListener('change', this.onStackChange)
    } catch {
      /* no matchMedia: the split is whatever the stylesheet says */
    }
  }

  /**
   * Build the panel and the control bar toggle. Called once per player mount;
   * a rebuild (source switch, cast handover) calls it again with a fresh
   * control bar, and the iframe is preserved across those so the room is not
   * rejoined and the scrollback is not lost.
   */
  mount(playerEl: Element): void {
    this.ensurePanel()
    this.mountButton(playerEl)
    this.applySize()
    this.apply()
  }

  /** Idempotent: an existing frame is never rebuilt, or the room is rejoined
   *  and the scrollback is lost on every source switch. */
  private ensurePanel(): void {
    if (this.unavailable) return
    if (!this.panel) {
      const panel = document.createElement('aside')
      panel.className = 'lp-chat'
      panel.setAttribute('aria-label', 'Live chat')

      /*
        The panel closes itself.

        The control bar is not always reachable: video.js hides it while the
        player is getting going (`lp-starting` sets opacity 0 and
        pointer-events none), which is exactly when a viewer waiting on a
        buffering stream is most likely to want the picture back. Leaving the
        only exit in the control bar meant the chat could be stuck open with no
        way to dismiss it. The header is always there, inside the panel, and
        does not depend on the player's state at all.
      */
      const header = document.createElement('div')
      header.className = 'lp-chat-header'

      const heading = document.createElement('span')
      heading.className = 'lp-chat-heading'
      heading.textContent = 'Live chat'

      const close = document.createElement('button')
      close.type = 'button'
      close.className = 'lp-chat-close'
      close.setAttribute('aria-label', 'Close live chat')
      close.title = 'Close live chat'
      close.innerHTML = ICON_CLOSE // static markup only
      close.addEventListener('click', (ev) => {
        ev.stopPropagation()
        this.close()
      })

      header.append(heading, close)
      panel.appendChild(header)
      panel.appendChild(this.buildResizer())

      const frame = document.createElement('iframe')
      frame.className = 'lp-chat-frame'
      frame.title = 'Live chat'
      frame.setAttribute('allow', 'clipboard-write')
      // No allow-same-origin with allow-scripts would defeat the chat app's own
      // storage; the frame is first-party, so the sandbox stays off and the
      // referrer is kept for the chat app's origin check.
      frame.src = chatRoomUrl(this.opts)
      panel.appendChild(frame)

      this.stage.appendChild(panel)
      this.panel = panel
      this.listenForChat(frame)
    }
  }

  /**
   * THE SPLIT HANDLE.
   *
   * One element on the panel's leading edge: the left border when the chat is
   * a column beside the video, the top border when it is a row underneath.
   * Which one is not a decision this code makes — the CSS switches the split
   * on aspect ratio, and this reads the same media query so the two can never
   * disagree about which edge the viewer is dragging.
   *
   * It is a real separator, not a decorative bar: it takes focus, answers to
   * the arrow keys, and reports its position to a screen reader. A pointer is
   * not the only way people resize a pane, and a drag target that only works
   * with a mouse is a drag target half the viewers cannot use.
   */
  private buildResizer(): HTMLElement {
    const grip = document.createElement('div')
    grip.className = 'lp-chat-resizer'
    grip.tabIndex = 0
    grip.setAttribute('role', 'separator')
    grip.setAttribute('aria-label', 'Resize live chat')
    grip.innerHTML = ICON_GRIP // static markup only

    grip.addEventListener('pointerdown', (ev) => this.beginResize(ev))
    grip.addEventListener('keydown', (ev) => this.nudgeResize(ev))
    // Back to the default split, the way a double-click resets a pane divider.
    grip.addEventListener('dblclick', () => this.setSize(null))

    this.resizer = grip
    this.syncResizer()
    return grip
  }

  /** True when the chat is stacked under the video rather than beside it. */
  private get stacked(): boolean {
    try {
      return window.matchMedia('(max-aspect-ratio: 1/1)').matches
    } catch {
      return false
    }
  }

  private beginResize(ev: PointerEvent): void {
    if (ev.button !== 0 && ev.pointerType === 'mouse') return
    const grip = this.resizer
    if (!grip) return
    ev.preventDefault()

    /*
      The video and the panel both animate their edges, which is right when the
      chat opens and wrong while a finger is dragging one: the split would
      arrive a third of a second after the pointer. Suspended for the drag.
    */
    this.stage.classList.add('lp-chat-resizing')
    try {
      grip.setPointerCapture(ev.pointerId)
    } catch {
      /* capture is a nicety; the move listener still tracks */
    }

    const onMove = (move: PointerEvent): void => {
      const box = this.stage.getBoundingClientRect()
      const raw = this.stacked
        ? (box.bottom - move.clientY) / (box.height || 1)
        : (box.right - move.clientX) / (box.width || 1)
      this.setSize(raw, { persist: false })
    }

    const onEnd = (): void => {
      this.releaseResize?.()
    }

    this.releaseResize = () => {
      this.releaseResize = null
      grip.removeEventListener('pointermove', onMove)
      grip.removeEventListener('pointerup', onEnd)
      grip.removeEventListener('pointercancel', onEnd)
      this.stage.classList.remove('lp-chat-resizing')
      if (this.size !== null) rememberSize(this.opts.embedId, this.size)
    }

    grip.addEventListener('pointermove', onMove)
    grip.addEventListener('pointerup', onEnd)
    grip.addEventListener('pointercancel', onEnd)
  }

  private nudgeResize(ev: KeyboardEvent): void {
    const stacked = this.stacked
    // Toward the edge the panel grows from, whichever axis it is on.
    const grow = stacked ? 'ArrowUp' : 'ArrowLeft'
    const shrink = stacked ? 'ArrowDown' : 'ArrowRight'
    let next: number | null | undefined
    if (ev.key === grow) next = (this.size ?? this.measuredSize()) + SPLIT_STEP
    else if (ev.key === shrink) next = (this.size ?? this.measuredSize()) - SPLIT_STEP
    else if (ev.key === 'Home' || ev.key === 'Escape') next = null
    if (next === undefined) return
    ev.preventDefault()
    this.setSize(next)
  }

  /** What the CSS default currently works out to, so a keyboard nudge starts
   *  from what the viewer can see rather than from a guess. */
  private measuredSize(): number {
    const panel = this.panel?.getBoundingClientRect()
    const box = this.stage.getBoundingClientRect()
    if (!panel || !box.width || !box.height) return 0.3
    return this.stacked ? panel.height / box.height : panel.width / box.width
  }

  /** null restores the CSS default. Anything else is clamped and applied. */
  private setSize(fraction: number | null, opts: { persist?: boolean } = {}): void {
    if (fraction === null) {
      this.size = null
      try {
        window.localStorage.removeItem(SIZE_KEY_PREFIX + this.opts.embedId)
      } catch {
        /* nothing to clear */
      }
    } else {
      this.size = Math.min(SPLIT_MAX, Math.max(SPLIT_MIN, fraction))
      if (opts.persist !== false) rememberSize(this.opts.embedId, this.size)
    }
    this.applySize()
  }

  /**
   * The size is written as a CSS variable, not as inline geometry, because the
   * split is four elements wide: the panel, the player, the poster wrapper and
   * the offline card all step aside together. One variable moves all of them,
   * and the stylesheet keeps owning which property each one uses.
   */
  private applySize(): void {
    const prop = this.stacked ? '--lp-chat-h' : '--lp-chat-w'
    const other = this.stacked ? '--lp-chat-w' : '--lp-chat-h'
    this.stage.style.removeProperty(other)
    if (this.size === null) this.stage.style.removeProperty(prop)
    else this.stage.style.setProperty(prop, `${(this.size * 100).toFixed(2)}%`)
    this.syncResizer()
  }

  private syncResizer(): void {
    const grip = this.resizer
    if (!grip) return
    const stacked = this.stacked
    grip.setAttribute('aria-orientation', stacked ? 'horizontal' : 'vertical')
    grip.setAttribute('aria-valuemin', String(Math.round(SPLIT_MIN * 100)))
    grip.setAttribute('aria-valuemax', String(Math.round(SPLIT_MAX * 100)))
    grip.setAttribute('aria-valuenow', String(Math.round((this.size ?? this.measuredSize()) * 100)))
    grip.title = stacked ? 'Drag to resize the chat height' : 'Drag to resize the chat width'
  }

  private listenForChat(frame: HTMLIFrameElement): void {
    if (this.onFrameMessage) window.removeEventListener('message', this.onFrameMessage)

    let origin: string
    try {
      origin = new URL(CHAT_BASE, location.href).origin
    } catch {
      return
    }

    this.onFrameMessage = (ev: MessageEvent): void => {
      if (ev.origin !== origin) return
      if (ev.source !== frame.contentWindow) return
      const data = ev.data as { type?: unknown; unread?: unknown } | null
      if (!data) return
      if (data.type === CHAT_CLOSE_MESSAGE) {
        this.close()
        return
      }
      if (data.type !== CHAT_MESSAGE) return
      const raw = Number(data.unread)
      if (!Number.isFinite(raw)) return
      this.setUnread(Math.max(0, Math.min(UNREAD_CAP + 1, Math.floor(raw))))
    }
    window.addEventListener('message', this.onFrameMessage)
  }

  private setUnread(count: number): void {
    // An open panel is being read; a count on top of it would be nonsense.
    this.unread = this.open ? 0 : count
    this.renderBadge()
  }

  private renderBadge(): void {
    if (!this.badge) return
    const show = !this.open && this.unread > 0 && !this.unavailable
    this.badge.textContent = this.unread > UNREAD_CAP ? `${UNREAD_CAP}+` : String(this.unread)
    this.badge.classList.toggle('lp-hidden', !show)
    this.button?.classList.toggle('lp-has-unread', show)
    // The accessible label carries the count too, and the count moves on its
    // own schedule — refresh it here or a screen reader reads a stale number.
    this.syncButton()
  }

  /** Tell the chat app the backlog has been seen, so it restarts from zero. */
  private markSeen(): void {
    this.unread = 0
    this.renderBadge()
    const frame = this.panel?.querySelector<HTMLIFrameElement>('.lp-chat-frame')
    try {
      frame?.contentWindow?.postMessage({ type: CHAT_MESSAGE, action: 'seen' }, new URL(CHAT_BASE, location.href).origin)
    } catch {
      /* frame not ready, or a bad base URL — the badge is already cleared */
    }
  }

  /** The toggle lives with the other right-hand icons, before the gear. */
  private mountButton(playerEl: Element): void {
    const controlBar = playerEl.querySelector('.vjs-control-bar')
    if (!controlBar) return
    controlBar.querySelector('.lp-chat-button')?.remove()

    const button = document.createElement('button')
    button.type = 'button'
    button.className = 'vjs-control vjs-button lp-chat-button'
    button.innerHTML = ICON_CHAT // static markup only

    const badge = document.createElement('span')
    badge.className = 'lp-chat-badge lp-hidden'
    badge.setAttribute('aria-hidden', 'true') // the label already says it
    button.appendChild(badge)
    this.badge = badge

    button.addEventListener('click', (ev) => {
      ev.stopPropagation()
      this.toggle()
    })

    const gear = controlBar.querySelector('.lp-settings-button')
    controlBar.insertBefore(button, gear)
    this.button = button
    this.syncButton()
    this.renderBadge()
  }

  /**
   * Watch-guard normally hands the same session id back on renewal, but it is
   * free not to. A new id is followed so a later reload carries a usable one;
   * the frame is rebuilt, which costs the scrollback, and the id itself is
   * already spent by then, so this changes nothing for a viewer who is
   * already in the room. It only matters for the join after this one.
   */
  setSession(sessionId: string): void {
    if (!sessionId || sessionId === this.opts.sessionId) return
    this.opts = { ...this.opts, sessionId }
    this.reloadFrame()
  }

  private reloadFrame(): void {
    const frame = this.panel?.querySelector<HTMLIFrameElement>('.lp-chat-frame')
    if (frame) frame.src = chatRoomUrl(this.opts)
  }

  toggle(): void {
    this.setWanted(!this.open)
  }

  /** Dismiss from inside the panel, independent of the control bar. */
  close(): void {
    this.setWanted(false)
  }

  private setWanted(wanted: boolean): void {
    this.wanted = wanted
    rememberOpen(this.opts.embedId, wanted)
    this.apply()
  }

  isOpen(): boolean {
    return this.open
  }

  /**
   * The broadcast ended, or came back. Offline closes the split and hides the
   * toggle without forgetting what the viewer wanted, so returning to air puts
   * the panel back exactly as they left it.
   */
  setStreamOffline(offline: boolean): void {
    if (offline === this.unavailable) return
    this.unavailable = offline
    if (offline) {
      // The broadcast is over. Drop the socket rather than leaving every idle
      // viewer holding a connection to a room nobody is speaking in.
      this.panel?.remove()
      this.panel = null
    } else {
      this.ensurePanel()
    }
    this.apply()
  }

  private apply(): void {
    const wasOpen = this.open
    this.open = !this.unavailable && this.wanted && !!this.panel
    this.stage.classList.toggle('with-chat', this.open)
    this.button?.classList.toggle('lp-hidden', this.unavailable)
    this.syncButton()
    if (this.open && !wasOpen) this.markSeen()
    else this.renderBadge()
  }

  private syncButton(): void {
    if (!this.button) return
    this.button.classList.toggle('lp-active', this.open)
    this.button.setAttribute('aria-pressed', this.open ? 'true' : 'false')
    const unread = !this.open && this.unread > 0 ? ` (${this.unread > UNREAD_CAP ? UNREAD_CAP + '+' : this.unread} new)` : ''
    const label = (this.open ? 'Hide live chat' : 'Show live chat') + unread
    this.button.setAttribute('aria-label', label)
    this.button.title = label
  }

  dispose(): void {
    this.releaseResize?.()
    if (this.stackQuery && this.onStackChange) {
      this.stackQuery.removeEventListener('change', this.onStackChange)
    }
    this.stackQuery = null
    this.onStackChange = null
    this.stage.style.removeProperty('--lp-chat-w')
    this.stage.style.removeProperty('--lp-chat-h')
    this.resizer = null
    if (this.onFrameMessage) window.removeEventListener('message', this.onFrameMessage)
    this.onFrameMessage = null
    this.badge = null
    this.unread = 0
    this.panel?.remove()
    this.panel = null
    this.button?.remove()
    this.button = null
    this.open = false
    this.stage.classList.remove('with-chat')
  }
}
